import React, { useEffect, useState, Dispatch, SetStateAction } from "react";
import { Button, Offcanvas } from "react-bootstrap";
import { ActionHandlerFunction, WebsocketCryptoMessage, IActionHandler } from "@preveil-api";
import {
  ApprovalGroup, ApprovalRequestStatus, RecoveryGroup, RequestTypes, SettingsUIActionTypes, UserRequest, useAppDispatch, useAppSelector, AccountWSSteps, Message,
  RequestTab, WebsocketErrors, MessageHandlerDisplayType, ConfigKeys
} from "src/common";
import { Loading } from "src/components";
import { SidePanelBody } from ".";
import { WebsocketStatus, websocketActions, uiActions, getConfigValue } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

type AllProps = {
  is_approval: boolean;
  request: UserRequest;
  current_recovery_group?: RecoveryGroup | ApprovalGroup;
  setShow: Dispatch<SetStateAction<boolean>>;
  setCheckedList: Dispatch<SetStateAction<UserRequest[]>>;
  confirmDelete: Function;
  handleAction: ActionHandlerFunction;
  is_details_loading: boolean
  get_responses: boolean
};

const FAILED_PIN_VERIFICATION = "pin verification failed";
function ApprovalRequestSidePanelComponent(props: AllProps) {
  const { is_approval, request, setShow, current_recovery_group,
    confirmDelete, setCheckedList, handleAction, is_details_loading, get_responses } = props;
  const dispatch = useAppDispatch();

  const websocket_active_status = new Set([
    AccountWSSteps.GET_CONNECTION_ESTABLISHED,
    AccountWSSteps.INITIALIZE_CONNECTION,
    AccountWSSteps.GET_ACCEPTED_INIT_OK,
    AccountWSSteps.ERROR
  ]);
  const current_account = useAppSelector((state: RootState) => state.account.current_account);
  const crypto_websocket_server = getConfigValue(ConfigKeys.crypto_websocket_server);
  // Websocket
  const message = useAppSelector((state: RootState) => state.websocket.message as WebsocketCryptoMessage);
  const status = useAppSelector((state: RootState) => state.websocket.status);
  const errors = useAppSelector((state: RootState) => state.websocket.errors);
  const [group, setGroup] = useState<RecoveryGroup | ApprovalGroup | undefined>(undefined);
  const approve_text =
    is_approval && request.type === RequestTypes.subsume_account
      ? "Join Organization"
      : "Approve";
  const reject_text =
    is_approval && request.type === RequestTypes.subsume_account
      ? "Reject"
      : "Vote No";


  // Local State
  const [step, setStep] = useState<number>(AccountWSSteps.DISCONNECTED);

  /* Description: On Initial Load, get the request responses to show the progress of the request - this is for recovery group
  user requests. */
  useEffect(() => {
    const group = request.current_group || current_recovery_group;
    setGroup(group);
    if (get_responses && !!group && request.status === ApprovalRequestStatus.pending) {
      handleAction({
        actionType: SettingsUIActionTypes.GetRequestResponses,
        params: request.request_id,
      });
    }
    setStep(AccountWSSteps.DISCONNECTED);
  }, []);

  useEffect(() => {
    const is_error = status === WebsocketStatus.error || message?.status === WebsocketStatus.error;
    let next = is_error ? AccountWSSteps.ERROR : !!message ? step + 1 : step;
    if (!!message?.status) {
      next = message.status === WebsocketStatus.key_transfer_complete ?
        AccountWSSteps.GET_KEY_TRANSFER_COMPLETE :
        message.status === WebsocketStatus.connection_established ?
          AccountWSSteps.GET_CONNECTION_ESTABLISHED : next;
    }

    if (!!message?.status && message.status === WebsocketStatus.verify_request) {
      const message = JSON.stringify({
        action: "approve"
      });
      dispatch(websocketActions.wsOnSendMessage(message));
    }
    if (next === AccountWSSteps.GET_KEY_TRANSFER_COMPLETE) {
      setShow(false);
      setCheckedList([]);
      handleAction({ actionType: SettingsUIActionTypes.Refresh, params: RequestTab.pending });
      dispatch(uiActions.handleSetMessage(new Message("Successfully Approved Export Request")));
    }

    setStep(next);

  }, [message, status]);

  // Description: First onload and unload events - destroy ws on leave
  useEffect(() => {
    return () => {  // Destroy websocket State on leave
      dispatch(websocketActions.destroyWebsocket());
    };
  }, []);

  useEffect(() => {
    if (!!errors && !!errors?.status && step === AccountWSSteps.ERROR) {
      const { msg } = errors;
      if (msg === FAILED_PIN_VERIFICATION) {
        dispatch(uiActions.handleRequestErrors(new Message(WebsocketErrors.bad_pin, MessageHandlerDisplayType.toastr), errors));
      } else {
        dispatch(uiActions.handleRequestErrors(new Message(WebsocketErrors.default, MessageHandlerDisplayType.toastr), errors));
      }
    }
  }, [errors, step]);

  const DataExportRequest = {
    handleInitWebsocketConnection: () => {
      handleDataExportRequest();
    },
    handleTransferKeyRequest: (params: { pin: string }) => {
      if (!!params.pin && params.pin.length > 0 && !!current_account?.user_id) {
        const verify_message: string = JSON.stringify({
          user_id: current_account.user_id,
          action: "verify",
          pin: params.pin,
        });
        dispatch(websocketActions.wsOnSendMessage(verify_message));
      } else {
        const error = {
          message: WebsocketErrors.bad_pin,
          stack: params
        };
        dispatch(uiActions.handleRequestErrors(new Message(WebsocketErrors.bad_pin, MessageHandlerDisplayType.toastr), error));
      }
    },
    handleDestroy: () => {
      setStep(AccountWSSteps.DISCONNECTED);
      dispatch(websocketActions.destroyWebsocket());
    },
  };

  function handleDataExportRequest() {
    if (!_.isEmpty(request) && request?.requester_id && !!current_account?.user_id) {
      const { requester_id } = request;
      const host = `${crypto_websocket_server}/users/${encodeURIComponent(current_account.user_id)}/connect/${encodeURIComponent(requester_id)}`;
      dispatch(websocketActions.wsConnect({ host, init: { action: "pre_init" } }));
    }
  }

  function respondToRequest(approved: boolean) {
    if (request && request.type !== RequestTypes.export) {
      setShow(false);
      setCheckedList([]);
      handleAction({
        actionType: SettingsUIActionTypes.RespondToRequest,
        params: { request, approved },
      });
    } else if (approved && request && request.type === RequestTypes.export) {
      handleDataExportRequest();
    } else if (!approved && request && request.type === RequestTypes.export) {
      setShow(false);
    }
  }

  function title(): string {
    return request.type !== RequestTypes.subsume_account
      ? `${is_approval ? "Approval: " : "Change Summary: "} ${request.mapped_action} Request`
      : `Invitation to join ${!!request.org_details && !!request.org_details.org_name ? `"${request.org_details.org_name}"` : "Organization"}`;
  }

  function handlePageActions(action_obj: IActionHandler) {
    const callback = `handle${action_obj.actionType}`;
    if ((DataExportRequest as any)[callback] instanceof Function) {
      (DataExportRequest as any)[callback](action_obj.params);
    }
  }

  // The Approve button will be hidden if the websocket screen is active
  // because it will have its own approve button.
  const show_approve_button = !websocket_active_status.has(step);
  const is_data_export_request = !!request && request?.type === RequestTypes.export;
  const approval_button_label = is_data_export_request ? "Start Approval" : approve_text;
  return (
    <>
      {!!is_details_loading ? <Loading className="in-place" /> :
        <>
          <Offcanvas.Header closeButton>
            <Offcanvas.Title className="semi-bold" as="h3">
              {title()}
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <SidePanelBody
              handleActions={handlePageActions}
              step={step}
              is_approval={is_approval}
              request={request}
              current_recovery_group={group}
            ></SidePanelBody>
            {is_approval && request.status === ApprovalRequestStatus.pending && show_approve_button && (
              <>
                <Button variant="primary" onClick={() => respondToRequest(true)}>
                  {approval_button_label}
                </Button>
                {!is_data_export_request && (
                  <Button variant="no-outline-primary" onClick={() => respondToRequest(false)}>
                    {reject_text}
                  </Button>
                )}
              </>
            )}
            {!is_approval && request.status === ApprovalRequestStatus.pending && (
              <Button variant="outline-secondary" onClick={() => confirmDelete()}>
                Delete Pending Changes
              </Button>
            )}
            {!!request.pending_response && (
              <p className="text-muted mt-3">
                <span className="text-primary">*</span>
                This request is pending a response from another approver.
              </p>
            )}
          </Offcanvas.Body>
        </>
      }
    </>
  );
}

export default React.memo(ApprovalRequestSidePanelComponent);
